import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { getElementId, getReactions, getTags } from '../../../common/elements/utils/elementPropertyUtils';
import { length, toArray } from '../../../common/utils/immutableHelper';
import { getCurrentUserId } from '../../user/currentUserSelector';
import { getGridSize } from '../../utils/grid/gridSizeSelector';
import { getUniqueReactions } from '../reactions/reactionUtil';
import { canWrite, canGiveFeedback } from '../../../common/permissions/permissionUtil';
import { fetchTagsFromDb } from '../tags/tagsService';

// Components
import ElementReactionAddButton from '../reactions/ElementReactionAddButton';
import ElementReactionPillContainer from '../reactions/ElementReactionPillContainer';
import TagElement from '../tags/TagElement';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { Tag } from '../../../common/tags/tagTypes';

import './PillsPanel.scss';

const mapStateToProps = createStructuredSelector({
    gridSize: getGridSize,
    currentUserId: getCurrentUserId,
});

type Props = {
    element: ImMNElement;
    elementId?: string;
    reactions?: object[] | object;
    currentUserId: string;
    gridSize: number;
    permissions: number;
};

type Reaction = {
    emojiCode: string;
    userIds: string[];
};

const ElementReactionList = (props: Props) => {
    const { element, elementId: _elementId, reactions: _reactions, currentUserId, gridSize, permissions } = props;
    const elementId = _elementId || getElementId(element);
    const reactions = _reactions || getReactions(element) || [];
    const reactionCount = length(reactions);

    const uniqueReactionsList: Reaction[] = React.useMemo(
        () => Object.values(getUniqueReactions(reactions)),
        [reactions],
    );

    const elementTagIds = getTags(element);
    const [elementTags, setElementTags] = React.useState<Tag[]>([]);
    const tagsCount = length(elementTags);

    // Temporary method of fetching tag data, to be replaced with redux later
    useEffect(() => {
        if (!elementTagIds || elementTagIds.size === 0) {
            if (elementTags.length) setElementTags([]);
            return;
        }

        fetchTagsFromDb({ tagIds: toArray(elementTagIds) }).then((tags) => {
            setElementTags(tags);
        });
    }, [elementTagIds]);

    if (!reactionCount && !tagsCount) return null;

    const userCanWriteReactions = canWrite(permissions) || canGiveFeedback(permissions);

    return (
        <div className="ElementReactions">
            <div className="reaction-button-container">
                {elementTags.map(({ _id, name, color }) => (
                    <div key={_id} className="mb-1 mr-1 max-w-full">
                        <TagElement name={name} color={color} />
                    </div>
                ))}
                {uniqueReactionsList.map(({ emojiCode, userIds }) => (
                    <ElementReactionPillContainer
                        elementId={elementId}
                        key={emojiCode}
                        disabled={!userCanWriteReactions}
                        emojiCode={emojiCode}
                        userIds={userIds}
                        currentUserId={currentUserId}
                        gridSize={gridSize}
                        permissions={permissions}
                    />
                ))}

                {userCanWriteReactions && !!reactionCount && <ElementReactionAddButton elementId={elementId} />}
            </div>
        </div>
    );
};

ElementReactionList.propTypes = {
    currentUserId: PropTypes.string,
    elementId: PropTypes.string,
    permissions: PropTypes.number,
    gridSize: PropTypes.number,
    reactions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    element: PropTypes.object,
    toggleReaction: PropTypes.func,
};

export default connect(mapStateToProps)(ElementReactionList);
